import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraAssistPanel, SpecraButton, SpecraIconButton } from '@mono/ui-components';
import { X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChatScroll } from '../../hooks/useChatScroll';
import { useChatHistory } from '../../hooks/useConversation';
import { useShanksChatConnect } from '../../hooks/useShanksChatConnect';
import { envelopeChip } from '../../lib/chat/envelope';
import { ChatHistoryUnavailable, ChatTurnError } from '../ChatView/ChatRecovery';
import { ChatThread } from '../ChatView/ChatThread';
import { convertShanksToChatMessages, mergeMessagesById } from '../ChatView/shanksMessageAdapter';
import { useQueuedChatFeedback } from '../ChatView/useQueuedChatFeedback';
import { ComposerInput } from '../ComposerInput';
import { askDisplayText, contextPrompt } from './EvidenceAsk';
import { useConversationSourceAccess } from './useConversationSourceAccess';
const requestAttempts = new Map();
export function AskSession({ chatId, envelope, onClose, context, request, onRequestHandled }) {
    const history = useChatHistory(chatId, sessionStorage.getItem(`assist-started::${chatId}`) !== 'true');
    const { scrollRef, awayFromBottom, jumpToLatest } = useChatScroll(chatId);
    const rememberConversation = useCallback(()=>sessionStorage.setItem(`assist-started::${chatId}`, 'true'), [
        chatId
    ]);
    const savedSubmission = history.data?.submissions?.find((s)=>s.state === 'running') ?? history.data?.submissions?.[0];
    const restoredSubmission = useMemo(()=>savedSubmission?.message ? {
            id: savedSubmission.submissionId,
            content: savedSubmission.message,
            fileIds: savedSubmission.fileIds,
            state: savedSubmission.state
        } : undefined, [
        savedSubmission
    ]);
    const shanks = useShanksChatConnect({
        chatId,
        initialConversationId: chatId,
        projectId: envelope.projectId,
        context: envelope,
        onConversationId: rememberConversation,
        onFinish: ()=>void history.refetch(),
        onError: ()=>void history.refetch(),
        restoredSubmission
    });
    const responseBusy = shanks.isLoading || !history.data?.readOnly && savedSubmission?.state === 'running';
    const unfinished = !history.data?.readOnly && savedSubmission && [
        'incomplete',
        'failed',
        'interrupted'
    ].includes(savedSubmission.state);
    const incomplete = shanks.incomplete || !shanks.error && unfinished && savedSubmission.state === 'incomplete';
    const turnError = responseBusy ? null : shanks.error ?? (unfinished ? incomplete ? 'This request needs more work. Your progress is saved. Continue to finish it.' : 'This response was interrupted. Retry your saved request.' : null);
    const messages = useMemo(()=>mergeMessagesById(history.data?.messages ?? [], convertShanksToChatMessages(shanks.messages), shanks.isLoading), [
        history.data?.messages,
        shanks.messages,
        shanks.isLoading
    ]);
    // Source access reads the raw turns; only what the user sees drops the plumbing.
    const displayMessages = useMemo(()=>messages.map((m)=>m.role === 'user' ? {
                ...m,
                parts: m.parts.map((p)=>p.type === 'text' ? {
                        ...p,
                        text: askDisplayText(p.text)
                    } : p)
            } : m), [
        messages
    ]);
    const sourceAccess = useConversationSourceAccess(messages, context.source?.id);
    const chip = envelopeChip(envelope);
    // Accept/Reject/Comment on a card in the rail continues this conversation.
    // Queue while the prior turn is still running so [delivery-action] lines
    // are never dropped (OpenAI turns can hang for minutes).
    const sendCardFeedback = useQueuedChatFeedback((message)=>{
        if (history.isError || sourceAccess.blocked) return;
        return shanks.sendMessage(message);
    }, responseBusy || history.isLoading, {
        disabled: history.isError || sourceAccess.blocked
    });
    const handleSubmit = async (data)=>{
        const content = data.prompt.trim();
        if (!content || sourceAccess.blocked || responseBusy || history.isLoading || history.isError || history.data?.readOnly) return;
        const fileIds = data.attachments?.map((a)=>a.id).filter((id)=>!!id);
        await shanks.sendMessage(contextPrompt(context, content), {
            fileIds
        });
    };
    const consumedRequest = useRef(0);
    const attemptedRequest = useRef(requestAttempts.get(chatId) ?? 0);
    const [skippedRequest, setSkippedRequest] = useState(requestAttempts.get(chatId));
    const [retryRevision, setRetryRevision] = useState(0);
    const requestRoot = useRef(null);
    useEffect(()=>{
        if (!request?.question || request.id === consumedRequest.current || request.id === attemptedRequest.current || sourceAccess.blocked || responseBusy || history.isLoading || history.isError || history.data?.readOnly) return;
        const pending = request;
        attemptedRequest.current = pending.id;
        requestAttempts.set(chatId, pending.id);
        setSkippedRequest(undefined);
        requestRoot.current?.focus();
        void shanks.sendMessage(contextPrompt(context, pending.question)).then((accepted)=>{
            if (accepted === false) {
                setSkippedRequest(pending.id);
            } else {
                consumedRequest.current = pending.id;
                onRequestHandled?.(pending.id);
            }
        }).catch(()=>setSkippedRequest(pending.id));
    }, [
        request,
        responseBusy,
        history.isLoading,
        history.isError,
        history.data?.readOnly,
        shanks.sendMessage,
        context,
        onRequestHandled,
        sourceAccess.blocked,
        retryRevision,
        chatId
    ]);
    const suggestions = [
        'Assess this pursuit using permitted evidence and identify gaps.',
        'Summarize customer interactions and follow-ups.',
        'Propose a source-backed draft for review; do not save it.'
    ];
    return /*#__PURE__*/ _jsxs("section", {
        ref: requestRoot,
        tabIndex: -1,
        "aria-label": "Capture assistant",
        children: [
            sourceAccess.blocked ? /*#__PURE__*/ _jsxs("output", {
                children: [
                    "Conversation withheld while a referenced source is unavailable or access is being checked. Your conversation and unsent draft are preserved.",
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: sourceAccess.retry,
                        children: "Recheck conversation sources"
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                hidden: sourceAccess.blocked,
                children: [
                    skippedRequest !== undefined && skippedRequest === request?.id ? /*#__PURE__*/ _jsxs("output", {
                        children: [
                            "Request was not submitted. Your request is preserved.",
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                disabled: responseBusy,
                                onClick: ()=>{
                                    attemptedRequest.current = 0;
                                    requestAttempts.delete(chatId);
                                    setRetryRevision((revision)=>revision + 1);
                                },
                                children: "Retry request"
                            })
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsxs(SpecraAssistPanel, {
                        className: "xl:w-full",
                        transcriptRef: scrollRef,
                        composer: history.isLoading || history.isError || history.data?.readOnly ? undefined : /*#__PURE__*/ _jsx(ComposerInput, {
                            dense: true,
                            mode: "chat",
                            onSubmit: handleSubmit,
                            placeholder: "Ask Specra…",
                            submitLabel: "Send",
                            submitting: responseBusy,
                            onStop: shanks.stop,
                            attachmentChatId: shanks.conversationId ?? chatId.replace(/^shanks-/, '')
                        }),
                        headerActions: /*#__PURE__*/ _jsx(SpecraIconButton, {
                            label: "Return to Evidence",
                            onClick: onClose,
                            size: "sm",
                            variant: "ghost",
                            children: /*#__PURE__*/ _jsx(X, {
                                "aria-hidden": "true"
                            })
                        }),
                        title: chip ? /*#__PURE__*/ _jsxs("span", {
                            className: "flex min-w-0 items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: "Assist"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "truncate rounded-full border border-border bg-muted px-2 py-0.5 text-sm font-normal text-muted-foreground",
                                    children: chip
                                })
                            ]
                        }) : 'Assist',
                        children: [
                            history.isLoading ? /*#__PURE__*/ _jsx("output", {
                                children: "Loading conversation…"
                            }) : null,
                            history.isError ? /*#__PURE__*/ _jsx(ChatHistoryUnavailable, {
                                error: history.error,
                                onRetry: ()=>void history.refetch()
                            }) : null,
                            sourceAccess.blocked || history.isLoading || history.isError ? null : messages.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "Ask about the selected pursuit. Answers may be incomplete when source evidence is unavailable."
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex flex-wrap gap-2",
                                        children: suggestions.map((q)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "secondary",
                                                size: "sm",
                                                onClick: ()=>void shanks.sendMessage(contextPrompt(context, q)),
                                                disabled: responseBusy || history.isLoading || history.isError || history.data?.readOnly,
                                                children: q
                                            }, q))
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(ChatThread, {
                                chatId: chatId,
                                isSending: responseBusy,
                                messages: displayMessages,
                                variant: "assist",
                                readOnly: history.data?.readOnly,
                                onFeedback: sendCardFeedback
                            }),
                            turnError ? /*#__PURE__*/ _jsx(ChatTurnError, {
                                error: turnError,
                                incomplete: incomplete,
                                onRetry: ()=>void shanks.retry()
                            }) : null,
                            awayFromBottom ? /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                size: "sm",
                                className: "sticky bottom-3 z-10",
                                onClick: jumpToLatest,
                                children: "Jump to latest"
                            }) : null
                        ]
                    })
                ]
            })
        ]
    });
}
